import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import i18n, { dbToI18n, i18nToDb, type DbLanguage } from '@/i18n';

interface AppContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isEmailConfirmed: boolean;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, displayName: string, birthDate?: string, churchId?: string, language?: DbLanguage) => Promise<{ error: string | null; needsConfirmation: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
  resendConfirmation: (email: string) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
  profile: any;
}

const AppContext = createContext<AppContextType | null>(null);

export const PROFILE_SELECT = 'user_id, username, display_name, avatar_url, bio, church_id, plano, language, tutorial_completed, is_founding_member, founding_member_number, created_at, updated_at, last_login_at, signup_source, signup_referrer, birth_date, phone';

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be within AppProvider');
  return ctx;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);

  const isEmailConfirmed = !!user?.email_confirmed_at;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const fetchUserData = useCallback(async (userId: string) => {
    setProfileLoading(true);
    try {
      const [rolesRes, profileRes] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', userId),
        supabase.from('profiles').select(PROFILE_SELECT).eq('user_id', userId).maybeSingle(),
      ]);

      if (rolesRes.error) console.error('Error fetching roles:', rolesRes.error);
      if (profileRes.error) console.error('Error fetching profile:', profileRes.error);

      const roles = rolesRes.data;
      const prof = profileRes.data;

      setIsAdmin(roles?.some(r => r.role === 'admin') ?? false);
      setProfile(prof);

      // Apply the user's saved language preference to the UI
      const lang = (prof as any)?.language as string | undefined;
      if (lang) {
        const target = dbToI18n(lang);
        if (i18n.language !== target) i18n.changeLanguage(target);
      }
    } catch (err) {
      console.error('Unexpected error in fetchUserData:', err);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Flush any pending legal acceptance saved at signup (before the session existed).
  const flushPendingLegalAcceptance = useCallback(async (userId: string) => {
    try {
      const raw = localStorage.getItem('pending_legal_acceptance');
      if (!raw) return;
      const parsed = JSON.parse(raw) as { terms?: string; privacy?: string };
      if (!parsed?.terms || !parsed?.privacy) {
        localStorage.removeItem('pending_legal_acceptance');
        return;
      }
      // Skip if already recorded for these versions
      const { data: existing } = await supabase
        .from('legal_acceptances')
        .select('document_type, version')
        .eq('user_id', userId);
      const has = (type: string, v: string) =>
        (existing ?? []).some((r: any) => r.document_type === type && r.version === v);
      const rows: any[] = [];
      if (!has('terms', parsed.terms)) {
        rows.push({ user_id: userId, document_type: 'terms', version: parsed.terms, user_agent: navigator.userAgent });
      }
      if (!has('privacy', parsed.privacy)) {
        rows.push({ user_id: userId, document_type: 'privacy', version: parsed.privacy, user_agent: navigator.userAgent });
      }
      if (rows.length > 0) {
        await supabase.from('legal_acceptances').insert(rows);
      }
      localStorage.removeItem('pending_legal_acceptance');
    } catch {
      // fail-silent — gate will handle re-acceptance if needed
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          void fetchUserData(session.user.id);
          // Track last login
          if (_event === 'SIGNED_IN') {
            supabase.from('profiles').update({ last_login_at: new Date().toISOString() }).eq('user_id', session.user.id).then(() => {});
            setTimeout(() => flushPendingLegalAcceptance(session.user.id), 0);
          }
        } else {
          setIsAdmin(false);
          setProfile(null);
          setProfileLoading(false);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
      }
      if (!session) {
        setLoading(false);
      } else {
        // If we have a session but auth state change didn't fire yet or we want to be sure
        setSession(session);
        setUser(session.user);
        fetchUserData(session.user.id).finally(() => setLoading(false));
      }
    }).catch(err => {
      console.error('Fatal error in getSession:', err);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData, flushPendingLegalAcceptance]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message.includes('Email not confirmed')) {
        return { error: 'Por favor, confirme seu e-mail antes de fazer login. Verifique sua caixa de entrada.' };
      }
      if (error.message.includes('Invalid login credentials')) {
        return { error: 'E-mail ou senha incorretos.' };
      }
      return { error: error.message };
    }
    return { error: null };
  };

  const signUp = async (email: string, password: string, displayName: string, birthDate?: string, churchId?: string, language?: DbLanguage) => {
    const lang: DbLanguage = language ?? i18nToDb(i18n.language);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: displayName, birth_date: birthDate, church_id: churchId, language: lang },
      },
    });

    if (error) {
      if (error.message.includes('already registered')) {
        return { error: 'Este e-mail já está cadastrado.', needsConfirmation: false };
      }
      return { error: error.message, needsConfirmation: false };
    }

    // If user exists but identities is empty, user already exists
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      return { error: 'Este e-mail já está cadastrado.', needsConfirmation: false };
    }

    // Persist language preference on the freshly-created profile.
    // (handle_new_user trigger inserts the row; we update it right after.)
    if (data.user) {
      supabase.from('profiles').update({ language: lang }).eq('user_id', data.user.id).then(() => {});
      i18n.changeLanguage(dbToI18n(lang));
    }

    return { error: null, needsConfirmation: true };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message ?? null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error?.message ?? null };
  };

  const resendConfirmation = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    return { error: error?.message ?? null };
  };

  const refreshProfile = useCallback(async () => {
    if (user) await fetchUserData(user.id);
  }, [user, fetchUserData]);

  return (
    <AppContext.Provider value={{
      isDarkMode, toggleDarkMode,
      user, session, isAdmin, isEmailConfirmed, loading, profileLoading,
      signIn, signUp, signOut,
      resetPassword, updatePassword, resendConfirmation,
      refreshProfile,
      profile,
    }}>
      {children}
    </AppContext.Provider>
  );
};
